import { useState } from 'react';
import { Plus, Pencil, Check, X, Star, Trash2, ChevronUp, ChevronDown, Settings } from 'lucide-react';
import type { SubscriptionPlan } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Input, Modal, Skeleton, EmptyState, Toggle, useConfirm, cn } from '@techsofit/ui';
import {
  getSubscriptionPlans,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
  getPlanFeatures,
  getPlatformSettings,
  updatePlatformSettings,
} from '../lib/adminApi';
import { useAsync } from '../lib/useAsync';
import { money } from '../lib/format';

export function SubscriptionPlansPage() {
  const { data: plans, loading, error, reload } = useAsync(getSubscriptionPlans);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const confirm = useConfirm();

  const list = plans ?? [];

  async function removePlan(p: SubscriptionPlan) {
    setActionError(null);
    const ok = await confirm({
      title: `Delete ${p.name}?`,
      description: 'This permanently removes the plan. Gyms already on it must be moved off first.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteSubscriptionPlan(p.id);
      reload();
    } catch (e) {
      setActionError(e instanceof ApiClientError ? e.message : 'Could not delete the plan.');
    }
  }

  // Move a card up/down and persist the new positions (sortOrder = index).
  async function reorder(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    setReordering(true);
    try {
      await Promise.all(
        next
          .map((p, i) => (p.sortOrder !== i ? updateSubscriptionPlan(p.id, { sortOrder: i }) : null))
          .filter(Boolean) as Promise<unknown>[],
      );
      reload();
    } finally {
      setReordering(false);
    }
  }

  return (
    <section className="mt-6 animate-fade-up">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-display-md text-ink">Subscription plans</h1>
          <p className="mt-1 text-sm text-ink-soft">The public pricing tiers gyms sign up to. Changes appear on the marketing site immediately.</p>
        </div>
        {!adding && (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setSettingsOpen(true)}>
              <Settings className="size-4" aria-hidden /> Platform settings
            </Button>
            <Button onClick={() => setAdding(true)}>
              <Plus className="size-4" aria-hidden /> Add plan
            </Button>
          </div>
        )}
      </div>

      {settingsOpen && <PlatformSettingsModal onClose={() => setSettingsOpen(false)} />}

      {actionError && (
        <div role="alert" className="mb-4 flex items-start justify-between gap-3 rounded-control bg-bad-soft px-4 py-3 text-sm font-medium text-bad-ink">
          <span>{actionError}</span>
          <button type="button" onClick={() => setActionError(null)} aria-label="Dismiss" className="shrink-0 opacity-70 hover:opacity-100">
            <X className="size-4" aria-hidden />
          </button>
        </div>
      )}

      {adding ? (
        <PlanForm
          mode="create"
          nextSortOrder={list.length}
          onCancel={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-64" />)}
        </div>
      ) : error ? (
        <EmptyState title="Couldn’t load plans" description={error} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p, i) =>
            editingId === p.id ? (
              <PlanForm
                key={p.id}
                mode="edit"
                plan={p}
                onCancel={() => setEditingId(null)}
                onDone={() => {
                  setEditingId(null);
                  reload();
                }}
              />
            ) : (
              <PlanCard
                key={p.id}
                plan={p}
                onEdit={() => setEditingId(p.id)}
                onDelete={() => removePlan(p)}
                onMoveUp={() => reorder(i, -1)}
                onMoveDown={() => reorder(i, 1)}
                canMoveUp={i > 0}
                canMoveDown={i < list.length - 1}
                busy={reordering}
              />
            ),
          )}
        </div>
      )}
    </section>
  );
}

function PlatformSettingsModal({ onClose }: { onClose: () => void }) {
  const { data, loading, reload } = useAsync(getPlatformSettings);
  const [discount, setDiscount] = useState('');
  const [trial, setTrial] = useState('');
  const [reminderLead, setReminderLead] = useState('');
  const [grace, setGrace] = useState('');
  const [saving, setSaving] = useState(false);

  const discountDisplay = discount === '' ? String(data?.annualDiscountPercent ?? 0) : discount;
  const trialDisplay = trial === '' ? String(data?.trialDays ?? 30) : trial;
  const reminderLeadDisplay = reminderLead === '' ? String(data?.subscriptionReminderLeadDays ?? 3) : reminderLead;
  const graceDisplay = grace === '' ? String(data?.subscriptionGraceDays ?? 4) : grace;

  async function save() {
    setSaving(true);
    try {
      await updatePlatformSettings({
        annualDiscountPercent: Math.max(0, Math.min(90, parseInt(discountDisplay, 10) || 0)),
        trialDays: Math.max(0, Math.min(365, parseInt(trialDisplay, 10) || 0)),
        subscriptionReminderLeadDays: Math.max(1, Math.min(60, parseInt(reminderLeadDisplay, 10) || 3)),
        subscriptionGraceDays: Math.max(0, Math.min(60, parseInt(graceDisplay, 10) || 0)),
      });
      reload();
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      className="sm:max-w-xl"
      title="Platform settings"
      description="Apply to every gym — pricing, trial length, and the billing reminder we send owners."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Close</Button>
          <Button onClick={save} loading={saving}>Save</Button>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Annual discount (%)"
          inputMode="numeric"
          value={loading ? '' : discountDisplay}
          onChange={(e) => setDiscount(e.target.value)}
          hint="Applied to every plan’s yearly price."
        />
        <Input
          label="Free trial (days)"
          inputMode="numeric"
          value={loading ? '' : trialDisplay}
          onChange={(e) => setTrial(e.target.value)}
          hint="Every new gym’s trial length."
        />
        <Input
          label="Subscription reminder (days before)"
          inputMode="numeric"
          value={loading ? '' : reminderLeadDisplay}
          onChange={(e) => setReminderLead(e.target.value)}
          hint="When WE remind a gym owner their TechSoFit plan is about to lapse (and again on the day). Not the member reminders each gym sets themselves."
        />
        <Input
          label="Grace period (days)"
          inputMode="numeric"
          value={loading ? '' : graceDisplay}
          onChange={(e) => setGrace(e.target.value)}
          hint="Days a gym keeps working after lapsing before it's locked. Reminded daily during this window."
        />
      </div>
    </Modal>
  );
}

function PlanCard({
  plan,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  busy,
}: {
  plan: SubscriptionPlan;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  busy: boolean;
}) {
  return (
    <Card className={cn('flex flex-col p-5', !plan.active && 'opacity-60')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 truncate font-display text-lg font-semibold text-ink">
            {plan.name}
            {plan.popular && <Star className="size-4 fill-accent text-accent" aria-hidden />}
          </p>
          {plan.tagline && <p className="truncate text-sm text-muted">{plan.tagline}</p>}
        </div>
        <div className="flex shrink-0 items-center">
          <button
            onClick={onMoveUp}
            disabled={!canMoveUp || busy}
            aria-label={`Move ${plan.name} up`}
            className="inline-flex size-8 items-center justify-center rounded-control text-muted hover:bg-surface hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <ChevronUp className="size-4" aria-hidden />
          </button>
          <button
            onClick={onMoveDown}
            disabled={!canMoveDown || busy}
            aria-label={`Move ${plan.name} down`}
            className="inline-flex size-8 items-center justify-center rounded-control text-muted hover:bg-surface hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <ChevronDown className="size-4" aria-hidden />
          </button>
          <button onClick={onEdit} aria-label={`Edit ${plan.name}`} className="inline-flex size-8 items-center justify-center rounded-control text-muted hover:bg-surface hover:text-ink">
            <Pencil className="size-4" aria-hidden />
          </button>
          <button onClick={onDelete} disabled={busy} aria-label={`Delete ${plan.name}`} className="inline-flex size-8 items-center justify-center rounded-control text-muted hover:bg-bad-soft hover:text-bad-ink disabled:opacity-30">
            <Trash2 className="size-4" aria-hidden />
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-display text-display-md tabular text-ink">{money(plan.monthlyPrice)}</span>
        <span className="text-sm text-muted">/mo</span>
      </div>
      <p className="mt-0.5 text-sm text-muted tabular">{money(plan.annualPrice)}/yr</p>
      <p className="mt-1 text-sm text-ink-soft">
        {plan.memberCap == null ? 'Unlimited members' : `Up to ${plan.memberCap} members`}
        {' · '}
        {plan.staffCap == null ? 'unlimited staff' : `${plan.staffCap} staff`}
      </p>

      <ul className="mt-4 space-y-2 border-t border-line pt-4">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-ink-soft">
            <Check className="mt-0.5 size-4 shrink-0 text-ok" aria-hidden /> {f}
          </li>
        ))}
      </ul>
      {!plan.active && <p className="mt-3 text-xs font-medium text-muted">Inactive — hidden from the marketing site</p>}
    </Card>
  );
}

interface PlanFormProps {
  mode: 'create' | 'edit';
  plan?: SubscriptionPlan;
  /** Position for a newly created plan (appended to the end). */
  nextSortOrder?: number;
  onCancel: () => void;
  onDone: () => void;
}

function PlanForm({ mode, plan, nextSortOrder, onCancel, onDone }: PlanFormProps) {
  const confirm = useConfirm();
  const [name, setName] = useState(plan?.name ?? '');
  const [tagline, setTagline] = useState(plan?.tagline ?? '');
  const [monthly, setMonthly] = useState(plan?.monthlyPrice ?? '');
  const [memberCap, setMemberCap] = useState(plan?.memberCap == null ? '' : String(plan.memberCap));
  const [staffCap, setStaffCap] = useState(plan?.staffCap == null ? '' : String(plan.staffCap));
  const [features, setFeatures] = useState<string[]>(plan?.features?.length ? plan.features : ['']);
  const [entitlements, setEntitlements] = useState<string[]>(plan?.entitlements ?? []);
  const { data: featureCatalog } = useAsync(getPlanFeatures);
  const [popular, setPopular] = useState(plan?.popular ?? false);
  const [active, setActive] = useState(plan?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setFeature(i: number, v: string) {
    setFeatures((fs) => fs.map((f, idx) => (idx === i ? v : f)));
  }
  function addFeature() {
    setFeatures((fs) => [...fs, '']);
  }
  function removeFeature(i: number) {
    setFeatures((fs) => (fs.length === 1 ? [''] : fs.filter((_, idx) => idx !== i)));
  }

  async function submit() {
    setError(null);
    if (!name.trim()) return setError('Give the plan a name.');
    if (!(Number(monthly) >= 0) || String(monthly).trim() === '') return setError('Enter a monthly price.');
    const featureList = features.map((f) => f.trim()).filter(Boolean);
    const cap = memberCap.trim() === '' ? null : Math.max(1, parseInt(memberCap, 10) || 1);
    const sCap = staffCap.trim() === '' ? null : Math.max(1, parseInt(staffCap, 10) || 1);
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        tagline: tagline.trim() || undefined,
        monthlyPrice: Number(monthly).toFixed(2),
        memberCap: cap,
        staffCap: sCap,
        features: featureList,
        entitlements,
        popular,
      };
      if (mode === 'create') {
        await createSubscriptionPlan({ ...body, sortOrder: nextSortOrder ?? 0 });
      } else {
        await updateSubscriptionPlan(plan!.id, { ...body, active });
      }
      onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the plan.');
      setSaving(false);
    }
  }

  async function remove() {
    if (!plan) return;
    const ok = await confirm({
      title: `Delete the “${plan.name}” plan?`,
      description: 'This can’t be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    setError(null);
    setDeleting(true);
    try {
      await deleteSubscriptionPlan(plan.id);
      onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not delete the plan.');
      setDeleting(false);
    }
  }

  const busy = saving || deleting;

  return (
    <Card className="mb-4 flex flex-col gap-4 p-5">
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Growth" />
      <Input label="Tagline" value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="Most gyms" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Monthly price" inputMode="decimal" leading="$" value={String(monthly)} onChange={(e) => setMonthly(e.target.value)} placeholder="31.00" />
        <Input label="Member cap" inputMode="numeric" value={memberCap} onChange={(e) => setMemberCap(e.target.value)} placeholder="Unlimited" hint="Blank = unlimited" />
        <Input label="Staff cap (incl. owner)" inputMode="numeric" value={staffCap} onChange={(e) => setStaffCap(e.target.value)} placeholder="Unlimited" hint="Blank = unlimited" />
      </div>

      {/* Features — one row each, add/remove */}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-ink-soft">Features</span>
        {features.map((f, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={f}
              onChange={(e) => setFeature(i, e.target.value)}
              placeholder={`Feature ${i + 1}`}
              className="h-touch w-full rounded-control border border-line bg-elevated px-4 text-ink placeholder:text-muted focus:border-ink-soft"
            />
            <button
              type="button"
              onClick={() => removeFeature(i)}
              aria-label="Remove feature"
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-control text-muted hover:bg-bad-soft hover:text-bad-ink"
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
        ))}
        <button type="button" onClick={addFeature} className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink">
          <Plus className="size-4" aria-hidden /> Add feature
        </button>
      </div>

      {/* Entitlements — enforceable capabilities this tier unlocks */}
      <div className="flex flex-col gap-3 rounded-control border border-line bg-elevated p-4">
        <div>
          <span className="text-sm font-medium text-ink-soft">What this plan unlocks</span>
          <p className="text-xs text-muted">Gyms on this tier get these capabilities. Enforced across the app.</p>
        </div>
        {(featureCatalog ?? []).map((f) => (
          <Toggle
            key={f.key}
            label={f.label}
            description={f.description}
            checked={entitlements.includes(f.key)}
            onChange={(on) =>
              setEntitlements((prev) => (on ? [...new Set([...prev, f.key])] : prev.filter((k) => k !== f.key)))
            }
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-5">
        <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-ink-soft">
          <input type="checkbox" checked={popular} onChange={(e) => setPopular(e.target.checked)} className="size-4 rounded border-line accent-ink" />
          Most popular
        </label>
        {mode === 'edit' && (
          <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-ink-soft">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="size-4 rounded border-line accent-ink" />
            Active (shown on marketing site)
          </label>
        )}
      </div>

      {error && (
        <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>
      )}

      <div className="flex items-center justify-end gap-2">
        {mode === 'edit' && (
          <Button size="sm" variant="ghost" onClick={remove} loading={deleting} disabled={busy} className="mr-auto text-bad-ink hover:bg-bad-soft">
            {!deleting && <Trash2 className="size-4" aria-hidden />} Delete
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
          <X className="size-4" aria-hidden /> Cancel
        </Button>
        <Button size="sm" onClick={submit} loading={saving} disabled={busy}>
          {!saving && <Check className="size-4" aria-hidden />} {mode === 'create' ? 'Add plan' : 'Save'}
        </Button>
      </div>
    </Card>
  );
}
